import type { ProjectTemplate, ProjectWorkflow } from '~/types/models';

function milestone(id: string, name: string, order: number, requiresClientApproval = false, description?: string) {
  return {
    id,
    name,
    order,
    description,
    requiresClientApproval,
  };
}

function task(
  id: string,
  title: string,
  milestoneId: string,
  options?: {
    description?: string;
    priority?: 'low' | 'medium' | 'high' | 'critical';
    dueOffsetDays?: number;
    clientVisible?: boolean;
  },
) {
  return {
    id,
    title,
    milestoneId,
    description: options?.description,
    priority: options?.priority,
    dueOffsetDays: options?.dueOffsetDays,
    clientVisible: options?.clientVisible ?? false,
  };
}

export const STARTER_WORKFLOWS: Array<Omit<ProjectWorkflow, 'id' | 'slug' | 'tenantId' | 'createdBy' | 'updatedBy' | 'createdAt' | 'updatedAt'>> = [
  {
    name: 'Web Design Workflow',
    category: 'Studio',
    description: 'A clear delivery flow for brochure sites, landing pages, and marketing websites.',
    milestones: [
      milestone('discovery', 'Discovery', 1, false, 'Align scope, goals, and content inputs.'),
      milestone('wireframes', 'Wireframes', 2, true, 'Shape the structure and page hierarchy.'),
      milestone('design', 'Visual Design', 3, true, 'Design polished desktop and mobile views.'),
      milestone('build', 'Build', 4, false, 'Translate approved designs into the live website.'),
      milestone('launch', 'Launch', 5, true, 'Final QA, signoff, and launch handover.'),
    ],
    tasks: [
      task('kickoff', 'Run kickoff and collect content', 'discovery', { priority: 'high', dueOffsetDays: 3 }),
      task('audit', 'Review references and current website', 'discovery', { dueOffsetDays: 5 }),
      task('wireframes-home', 'Create homepage wireframe', 'wireframes', { priority: 'high', dueOffsetDays: 8 }),
      task('wireframes-inner', 'Create inner page wireframes', 'wireframes', { dueOffsetDays: 10 }),
      task('design-system', 'Build design direction and type system', 'design', { priority: 'high', dueOffsetDays: 14 }),
      task('responsive-design', 'Prepare responsive screen designs', 'design', { dueOffsetDays: 16 }),
      task('dev-build', 'Build approved pages', 'build', { priority: 'high', dueOffsetDays: 21 }),
      task('qa-pass', 'Run pre-launch QA', 'launch', { dueOffsetDays: 24 }),
      task('handover', 'Deliver launch notes and handover pack', 'launch', { clientVisible: true, dueOffsetDays: 25 }),
    ],
  },
  {
    name: 'SEO Management Workflow',
    category: 'Growth',
    description: 'An ongoing optimisation workflow for monthly SEO strategy and execution.',
    milestones: [
      milestone('audit', 'Audit', 1, false, 'Assess current performance, issues, and opportunities.'),
      milestone('strategy', 'Strategy', 2, true, 'Set priorities and monthly goals.'),
      milestone('execution', 'Execution', 3, false, 'Publish improvements and optimisation tasks.'),
      milestone('reporting', 'Reporting', 4, true, 'Share results and next-month actions.'),
    ],
    tasks: [
      task('seo-audit', 'Run technical SEO audit', 'audit', { priority: 'high', dueOffsetDays: 2 }),
      task('keyword-review', 'Review target keyword clusters', 'strategy', { dueOffsetDays: 5 }),
      task('content-briefs', 'Prepare content and optimisation briefs', 'execution', { priority: 'high', dueOffsetDays: 10 }),
      task('on-page', 'Ship on-page improvements', 'execution', { dueOffsetDays: 14 }),
      task('report', 'Prepare monthly SEO report', 'reporting', { clientVisible: true, dueOffsetDays: 20 }),
      task('next-actions', 'Recommend next sprint actions', 'reporting', { clientVisible: true, dueOffsetDays: 21 }),
    ],
  },
  {
    name: 'Brand Identity Workflow',
    category: 'Brand',
    description: 'A structured process for strategy, concepts, refinement, and final asset delivery.',
    milestones: [
      milestone('brand-discovery', 'Discovery', 1, false, 'Understand the business, audience, and goals.'),
      milestone('brand-strategy', 'Strategy', 2, true, 'Clarify positioning and creative direction.'),
      milestone('concepts', 'Concepts', 3, true, 'Develop and present visual concepts.'),
      milestone('refinement', 'Refinement', 4, true, 'Refine the chosen route and supporting assets.'),
      milestone('handover', 'Handover', 5, true, 'Deliver final files, guidelines, and rollout notes.'),
    ],
    tasks: [
      task('brand-questionnaire', 'Complete discovery questionnaire review', 'brand-discovery', { dueOffsetDays: 3 }),
      task('competitive-review', 'Audit category and competitor brands', 'brand-strategy', { dueOffsetDays: 5 }),
      task('creative-directions', 'Present 2-3 creative directions', 'concepts', { priority: 'high', dueOffsetDays: 10 }),
      task('logo-refinement', 'Refine selected logo route', 'refinement', { dueOffsetDays: 15 }),
      task('brand-assets', 'Package final brand assets', 'handover', { clientVisible: true, dueOffsetDays: 18 }),
      task('guidelines', 'Deliver brand guidelines', 'handover', { clientVisible: true, dueOffsetDays: 19 }),
    ],
  },
];

export const STARTER_TEMPLATES: Array<Omit<ProjectTemplate, 'id' | 'slug' | 'tenantId' | 'createdBy' | 'updatedBy' | 'createdAt' | 'updatedAt'>> = [
  {
    name: '5-Page Website Launch',
    description: 'A polished starter for brochure-style web design and build projects.',
    workflowName: 'Web Design Workflow',
    projectDescription: 'A structured website project covering discovery, design, build, and launch.',
    defaultStatus: 'Active',
    defaultHealth: 'healthy',
    defaultStage: 'Discovery',
    quoteDefaults: {
      enabled: true,
      autoCreate: true,
      title: '5-Page Website Proposal',
      issueDateOffsetDays: 0,
      expiryDateOffsetDays: 14,
      lineItems: [
        { name: 'Website strategy and planning', quantity: 1, price: 650 },
        { name: 'Design and build of five pages', quantity: 1, price: 2450 },
        { name: 'Launch QA and handover', quantity: 1, price: 400 },
      ],
      deposit: {
        enabled: true,
        mode: 'percentage',
        value: 50,
      },
    },
    milestones: STARTER_WORKFLOWS[0].milestones,
    tasks: STARTER_WORKFLOWS[0].tasks,
  },
  {
    name: 'Monthly SEO Retainer',
    description: 'A repeatable monthly delivery structure for SEO retainers and reporting.',
    workflowName: 'SEO Management Workflow',
    projectDescription: 'An ongoing SEO engagement with strategy, execution, and monthly reporting.',
    defaultStatus: 'Active',
    defaultHealth: 'healthy',
    defaultStage: 'Audit',
    quoteDefaults: {
      enabled: true,
      autoCreate: true,
      title: 'Monthly SEO Retainer Proposal',
      issueDateOffsetDays: 0,
      expiryDateOffsetDays: 7,
      lineItems: [
        { name: 'Monthly SEO strategy and reporting', quantity: 1, price: 900 },
        { name: 'On-page optimisation sprint', quantity: 1, price: 600 },
      ],
      paymentPlan: {
        enabled: true,
        installments: [
          { label: 'Month 1', amount: 750, status: 'pending' },
          { label: 'Month 2', amount: 750, status: 'pending' },
        ],
      },
    },
    milestones: STARTER_WORKFLOWS[1].milestones,
    tasks: STARTER_WORKFLOWS[1].tasks,
  },
];
